import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Pager, { paginate } from './Pager';
import { useFeature } from '../tenant';

const MD = {
  primary: '#18181B', accent: '#A16207', onVariant: 'var(--muted-fg)',
  card: { background: 'var(--card)', borderRadius: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.07)' },
  btn: { width: '100%', padding: 13, borderRadius: 14, border: 'none', background: 'var(--primary)', color: 'var(--on-primary)', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.92rem' },
};
const fa = n => Number(n || 0).toLocaleString('fa-IR');
const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('customer_token') || ''}` } });
const REASON_FA = { earn: 'کسب از خرید', spin_cost: 'هزینه گردونه', spin_win: 'جایزه گردونه', redeem: 'خرید کد تخفیف', adjust: 'هدیه مدیریت', refund_void: 'ابطال (استرداد)' };

const SEG_A = '#232326';   // dark slice
const SEG_B = '#B8860B';   // gold slice
const SEG_TXT = '#FFF8E7';

/** A real spinning prize wheel (SVG). Rotates with CSS transition and lands
 *  the won slice under the top pointer. `rotation` is controlled by parent. */
function Wheel({ prizes, rotation, spinning, onSpin, canSpin, cost }) {
  const n = Math.max(1, prizes.length);
  const seg = 360 / n;
  const R = 96, CX = 100, CY = 100;
  const rad = d => (d * Math.PI) / 180;
  const pt = (angle, r) => [CX + r * Math.cos(rad(angle)), CY + r * Math.sin(rad(angle))];
  return (
    <div style={{ textAlign: 'center', position: 'relative' }}>
      <style>{`@keyframes wheelGlow{0%,100%{filter:drop-shadow(0 0 0 rgba(184,134,11,0))}50%{filter:drop-shadow(0 0 14px rgba(184,134,11,0.55))}}@keyframes popIn{0%{transform:scale(0.85);opacity:0}100%{transform:scale(1);opacity:1}}`}</style>
      {/* pointer */}
      <div style={{ position: 'absolute', top: -6, left: '50%', transform: 'translateX(-50%)', zIndex: 3 }}>
        <div style={{ width: 0, height: 0, borderLeft: '13px solid transparent', borderRight: '13px solid transparent', borderTop: '22px solid #B8860B', filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.3))' }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#232326', margin: '-24px auto 0', position: 'relative' }} />
      </div>
      <svg viewBox="0 0 200 200" style={{ width: '100%', maxWidth: 330, height: 'auto', display: 'block', margin: '6px auto 0', animation: spinning ? 'wheelGlow 0.9s ease-in-out infinite' : 'none' }}>
        <circle cx={CX} cy={CY} r={R + 4} fill="#B8860B" />
        <circle cx={CX} cy={CY} r={R + 1.5} fill="#232326" />
        <g style={{ transform: `rotate(${rotation}deg)`, transformOrigin: '100px 100px', transition: spinning ? 'transform 4.4s cubic-bezier(0.12,0.8,0.16,1)' : 'none' }}>
          {prizes.map((p, i) => {
            const a0 = i * seg - 90, a1 = (i + 1) * seg - 90;
            const [x0, y0] = pt(a0, R - 2), [x1, y1] = pt(a1, R - 2);
            const large = seg > 180 ? 1 : 0;
            const mid = (a0 + a1) / 2;
            const [tx, ty] = pt(mid, 58);
            return (
              <g key={p.id}>
                <path d={`M ${CX} ${CY} L ${x0.toFixed(2)} ${y0.toFixed(2)} A ${R - 2} ${R - 2} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)} Z`}
                  fill={i % 2 ? SEG_B : SEG_A} stroke="#FFF8E7" strokeWidth="1" />
                <text x={tx.toFixed(1)} y={ty.toFixed(1)} transform={`rotate(${mid + 90} ${tx.toFixed(1)} ${ty.toFixed(1)})`}
                  textAnchor="middle" dominantBaseline="middle" fill={SEG_TXT}
                  fontSize={seg < 45 ? 9 : 11} fontWeight="800" fontFamily="inherit">
                  {String(p.label || '').slice(0, 14)}
                </text>
                <text x={pt(mid, 30)[0].toFixed(1)} y={pt(mid, 30)[1].toFixed(1)} transform={`rotate(${mid + 90} ${pt(mid, 30)[0].toFixed(1)} ${pt(mid, 30)[1].toFixed(1)})`}
                  textAnchor="middle" dominantBaseline="middle" fontSize="13">
                  {p.type === 'discount' ? '🎟' : p.type === 'points' ? '⭐' : '💨'}
                </text>
              </g>
            );
          })}
          {Array.from({ length: n }).map((_, i) => {
            const [dx, dy] = pt(i * seg - 90, R - 2);
            return <circle key={i} cx={dx.toFixed(1)} cy={dy.toFixed(1)} r={2.4} fill="#FFF8E7" />;
          })}
        </g>
        {/* hub + spin button */}
        <circle cx={CX} cy={CY} r={30} fill="#232326" stroke="#B8860B" strokeWidth={3} />
        <g onClick={() => { if (canSpin && !spinning) onSpin(); }}
          style={{ cursor: canSpin && !spinning ? 'pointer' : 'default', opacity: canSpin ? 1 : 0.55 }}>
          <text x={CX} y={spinning ? CY - 2 : CY - 4} textAnchor="middle" dominantBaseline="middle" fill="#FFD75E" fontSize={spinning ? 11 : 15} fontWeight="800" fontFamily="inherit">
            {spinning ? '...' : 'بچرخان!'}
          </text>
          {!spinning && (
            <text x={CX} y={CY + 12} textAnchor="middle" dominantBaseline="middle" fill="#FFF8E7" fontSize="8.5" fontFamily="inherit">
              {fa(cost)} امتیاز
            </text>
          )}
        </g>
      </svg>
      <div style={{ fontSize: '0.72rem', color: '#A1A1AA', marginTop: 6 }}>روی «بچرخان» وسط گردونه بزنید 🎡</div>
    </div>
  );
}

/** Customer loyalty club: balance, lucky wheel, discount shop, history. */
export default function ClubPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null); // {prize, code}
  const [copied, setCopied] = useState(false);
  const [logPage, setLogPage] = useState(1);
  const wheelOn = useFeature('wheel'); // site-builder plan gate
  const timer = useRef(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await axios.get('/loyalty', authH());
      setData(r.data);
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.message || 'خطا در دریافت باشگاه.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' });
    }
    if (!silent) setLoading(false);
  };
  useEffect(() => {
    load();
    return () => { if (timer.current) clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const spin = async () => {
    if (busy || spinning) return;
    if ((data?.balance || 0) < (data?.wheel_cost || 0)) {
      toast.error('امتیاز کافی برای چرخاندن گردونه ندارید.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' });
      return;
    }
    setBusy(true); setResult(null);
    let res;
    try {
      const r = await axios.post('/loyalty/spin', {}, authH());
      res = r.data;
    } catch (e) {
      toast.error(e.response?.data?.message || 'خطا.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' });
      setBusy(false);
      return;
    }
    // Animate: rotate the wheel ~5 turns and land the won slice under the pointer
    const prizes = data.wheel_prizes || [];
    const n = Math.max(1, prizes.length);
    const seg = 360 / n;
    const winIdx = Math.max(0, prizes.findIndex(p => String(p.id) === String(res.prize?.id)));
    // slice i centre sits at (i*seg + seg/2 - 90); pointer is at top (-90).
    // final rotation R must satisfy: centre + R ≡ -90  →  R ≡ -(winIdx*seg + seg/2)
    const jitter = (Math.random() * 2 - 1) * Math.max(0, seg / 2 - 6);
    const target = -(winIdx * seg + seg / 2) + jitter;
    setRotation(prev => {
      const norm = ((target - prev) % 360 + 360) % 360;
      return prev + 360 * 5 + norm;
    });
    setResult(null);
    setSpinning(true);
    timer.current = setTimeout(() => {
      setSpinning(false); setBusy(false);
      setResult(res);
      setCopied(false);
      // Local-first update: no backend refetch, no loader flash.
      // Balance comes straight from the spin response; history rows are
      // prepended optimistically (server state syncs on next mount).
      const now = new Date().toISOString();
      const rows = [{ id: `local-${Date.now()}-c`, reason: 'spin_cost', points: -(data?.wheel_cost || 0), created_at: now }];
      if (res.prize?.type === 'points' && Number(res.prize?.points || 0) > 0) {
        rows.unshift({ id: `local-${Date.now()}-w`, reason: 'spin_win', points: Number(res.prize.points), created_at: now });
      }
      setData(d => d ? ({
        ...d,
        balance: res.balance,
        history: { logs: [...rows, ...((d.history && d.history.logs) || [])] },
      }) : d);
      setLogPage(1);
    }, 4600);
  };

  const copyCode = () => {
    const c = result?.code;
    if (!c) return;
    try {
      if (navigator.clipboard?.writeText) navigator.clipboard.writeText(c);
      else {
        const ta = document.createElement('textarea');
        ta.value = c; document.body.appendChild(ta); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const redeem = async id => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post('/loyalty/redeem', { item_id: id }, authH());
      setResult({ prize: { label: r.data.item.title, type: 'discount' }, code: r.data.code });
      toast.success(`کد تخفیف شما: ${r.data.code}`, { position: 'bottom-right', autoClose: 5000, theme: 'colored' });
      // Local-first: no refetch, no loader. Balance from response, log prepended.
      const cost = Number(r.data.item?.cost || 0);
      setData(d => d ? ({
        ...d,
        balance: r.data.balance ?? ((d.balance || 0) - cost),
        history: { logs: [{ id: `local-${Date.now()}`, reason: 'redeem', points: -cost, created_at: new Date().toISOString() }, ...((d.history && d.history.logs) || [])] },
      }) : d);
      setLogPage(1);
    } catch (e) {
      toast.error(e.response?.data?.message || 'خطا.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' });
    }
    setBusy(false);
  };

  if (loading) return <div style={{ ...MD.card, padding: 36, textAlign: 'center', color: MD.onVariant }}>در حال بارگذاری باشگاه...</div>;
  if (!data?.enabled) return <div style={{ ...MD.card, padding: 36, textAlign: 'center', color: MD.onVariant }}>باشگاه مشتریان فعلاً فعال نیست.</div>;

  const prizes = data.wheel_prizes || [];
  const logs = data.history?.logs || [];
  const logSlice = paginate(logs, logPage, 6);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <ToastContainer position="bottom-right" autoClose={5000} rtl theme="colored" />
      <div style={{ background: '#18181B', borderRadius: 16, padding: 22, color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>موجودی امتیاز شما</div>
          <div style={{ fontSize: '1.9rem', fontWeight: 800 }}>{fa(data.balance)}</div>
        </div>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.6rem', fontWeight: 800, color: '#FFE082' }}>★</div>
      </div>

      {data.wheel_enabled && !!prizes.length && wheelOn !== false && (
        <div style={{ ...MD.card, padding: 18, background: 'linear-gradient(180deg, #FFFBEB 0%, var(--card) 55%)' }}>
          <h3 style={{ margin: '0 0 2px', fontSize: '1.05rem', fontWeight: 800, textAlign: 'center' }}>🎡 گردونه شانس</h3>
          <p style={{ margin: '0 0 4px', fontSize: '0.78rem', color: MD.onVariant, textAlign: 'center' }}>شانست رو امتحان کن — هزینه هر چرخش <b>{fa(data.wheel_cost)} امتیاز</b></p>
          <Wheel prizes={prizes} rotation={rotation} spinning={spinning}
            onSpin={spin} canSpin={(data.balance || 0) >= (data.wheel_cost || 0)} cost={data.wheel_cost} />
          {result && (
            <div style={{ background: 'linear-gradient(135deg, #166534 0%, #15803D 100%)', borderRadius: 14, padding: '14px 16px', marginTop: 12, textAlign: 'center', color: '#fff', animation: 'popIn 0.35s ease', boxShadow: '0 8px 24px rgba(22,101,52,0.35)' }}>
              <div style={{ fontSize: '1.6rem' }}>🎉</div>
              <div style={{ fontWeight: 800, fontSize: '0.95rem' }}>تبریک! «{result.prize?.label}» بردی</div>
              {result.code && (
                <button onClick={copyCode} dir="ltr"
                  style={{ marginTop: 8, padding: '9px 18px', borderRadius: 10, border: '1.5px dashed rgba(255,255,255,0.7)', background: 'rgba(255,255,255,0.14)', color: '#fff', fontWeight: 800, fontSize: '1.05rem', letterSpacing: 1.5, cursor: 'pointer', fontFamily: 'inherit' }}>
                  {result.code} {copied ? '✓ کپی شد' : '⧉'}
                </button>
              )}
              <div>
                <button onClick={spin} disabled={busy || spinning || (data.balance || 0) < (data.wheel_cost || 0)}
                  style={{ marginTop: 10, padding: '9px 26px', borderRadius: 12, border: 'none', background: '#FFD75E', color: '#232326', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.85rem', opacity: (busy || spinning) ? 0.6 : 1 }}>
                  یه بار دیگه! 🎡
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {data.shop_enabled && !!(data.shop_items || []).length && (
        <div style={{ ...MD.card, padding: 18 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 800 }}>خرید کد تخفیف با امتیاز</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(data.shop_items || []).map(it => (
              <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, border: '1px solid #F4F4F5', borderRadius: 12, padding: '10px 14px', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>{it.title}</div>
                  <div style={{ fontSize: '0.76rem', color: MD.onVariant }}>%{fa(it.discount_percent)} تخفیف · هزینه: {fa(it.cost)} امتیاز</div>
                </div>
                <button onClick={() => redeem(it.id)} disabled={busy || (data.balance || 0) < it.cost}
                  style={{ padding: '9px 22px', borderRadius: 12, border: 'none', background: 'var(--primary)', color: 'var(--on-primary)', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.82rem', opacity: (busy || (data.balance || 0) < it.cost) ? 0.5 : 1 }}>
                  خرید
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ ...MD.card, padding: 18 }}>
        <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 800 }}>تاریخچه امتیازها</h3>
        {logs.length ? (<>
          {logSlice.items.map(l => (
            <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid #F4F4F5', fontSize: '0.82rem' }}>
              <span style={{ color: MD.onVariant }}>{REASON_FA[l.reason] || l.reason} · {l.created_at ? new Date(l.created_at).toLocaleDateString('fa-IR') : ''}</span>
              <b style={{ color: Number(l.points) < 0 ? '#991B1B' : '#166534' }} dir="ltr">{Number(l.points) > 0 ? `+${fa(l.points)}` : fa(l.points)}</b>
            </div>
          ))}
          <Pager page={logSlice.page} pages={logSlice.pages} onChange={setLogPage} />
        </>) : <div style={{ color: MD.onVariant, fontSize: '0.85rem', textAlign: 'center' }}>هنوز فعالیتی ثبت نشده است.</div>}
      </div>
    </div>
  );
}
